import { useEffect, useState } from "react";
import { Sparkles, Loader2, ExternalLink } from "lucide-react";
import { api, errText } from "../../lib/api";
import { Badge, stamp } from "./ui";
import IndexPushButton from "./IndexPush";
import { ClaudeModelSelect } from "./ClaudeModelSelect";
import { ContentJobStatus } from "./ContentJobStatus";

// Local-SEO copy per Moroccan city: unique intro, neighbourhoods, local occasions, tips and 5 city-specific FAQ (×4 locales).
export default function CityPagesPage() {
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState("");
  const [model, setModel] = useState("");
  const load = () => api.get("/admin/cities/local-copy").then((r) => setData(r.data)).catch((e) => setMsg(errText(e)));
  useEffect(() => { load(); }, []);
  const running = data?.job && ["queued", "running"].includes(data.job.status);
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [running]);

  const genOne = async (c) => {
    setBusy(c.id); setMsg("");
    try { await api.post(`/admin/cities/${c.id}/local-copy`, { model: model || null }); setMsg(`${c.name}: writing and validating all four languages…`); load(); }
    catch (e) { setMsg(errText(e)); } finally { setBusy(""); }
  };
  const genAll = async () => {
    if (!window.confirm("Generate content for every deliverable city using the selected Claude model? Usage is billed to your Anthropic account.")) return;
    setBusy("all"); setMsg("");
    try { await api.post("/admin/cities/local-copy/all", { model: model || null }); setMsg("Writing every deliverable city in the background…"); load(); }
    catch (e) { setMsg(errText(e)); } finally { setBusy(""); }
  };

  return (
    <div data-testid="admin-city-pages">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="font-display text-2xl text-ink mb-1">City pages · local SEO</h1>
          <p className="text-sm text-muted">Claude writes a unique, locally-grounded block for each city page (intro, neighbourhoods, local occasions, tips, 5 FAQ) in EN/FR/ES/AR. Snapshots are rebuilt on the next deploy.</p>
        </div>
        <button className="btn-primary text-xs px-4 inline-flex items-center gap-1.5" disabled={!!busy || running} onClick={genAll} data-testid="city-pages-generate-all">
          {busy === "all" || running ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Write all cities
        </button>
      </div>
      <div className="mb-4"><IndexPushButton testid="city-index-push" /></div>
      <div className="mb-4"><ClaudeModelSelect value={model} onChange={setModel} testid="city-claude-model" disabled={!!busy || running} /></div>
      {msg ? <p className="text-sm text-brass mb-3" data-testid="city-pages-message">{msg}</p> : null}
      <ContentJobStatus job={data?.job} model={model} onChanged={load} testid="city-pages-job" unit="cities" />
      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-start smallcaps border-b border-brass-light"><th className="p-3 text-start">City</th><th className="p-3 text-start">Headline (EN)</th><th className="p-3">FAQ</th><th className="p-3 text-start">Generated</th><th className="p-3" /></tr></thead>
          <tbody>
            {(data?.cities || []).map((c) => (
              <tr key={c.id} className="border-b border-brass-light/60" data-testid={`city-row-${c.id}`}>
                <td className="p-3 whitespace-nowrap">
                  <a href={`/en/flower-delivery/${c.name.toLowerCase().replace(/\s+/g, "-")}`} target="_blank" rel="noreferrer" className="underline decoration-brass underline-offset-2 inline-flex items-center gap-1">{c.name} <ExternalLink size={11} /></a>
                </td>
                <td className="p-3 text-muted">{c.headline || <span className="text-warning">not written yet</span>}</td>
                <td className="p-3 text-center tabular">{c.faq_count || 0}</td>
                <td className="p-3 text-muted whitespace-nowrap">{c.generated_at ? stamp(c.generated_at) : "—"}</td>
                <td className="p-3 text-end">
                  <button className="text-[11px] border border-brass px-2 py-1 rounded-sm text-brass hover:bg-brass hover:text-plaster transition-colors inline-flex items-center gap-1"
                          disabled={!!busy || running} onClick={() => genOne(c)} data-testid={`city-generate-${c.id}`}>
                    {busy === c.id ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} {c.generated_at ? "Rewrite" : "Write"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
